import * as React from 'react';
import Gateway from '../Gateway';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../theme';
import { CardPaymentMethodWithUrl, PaymentPage, PaymentType } from '../../../types';
import { useCallback, useEffect, useMemo } from 'react';
import SimpleLoader from '../../../../SimpleLoader';
import { useToggleable } from '../../../../hooks';
import { useConfig } from '../../../../context';
import { useRenderers } from '../../../../renderProps';
import { isCorrectFrameUrl } from '../../../utils';

interface Props {
	frameInfo: CardPaymentMethodWithUrl;
	onClick: (page: PaymentPage) => void;
	openFrameInNewTab?: boolean;
	isPreOpened?: boolean;
	gatewayTitle?: string;
	headerTitle?: string;
	onPaymentFrameLoaded?: () => void;
	onStartRedirecting?: () => void;
	isLoading?: boolean;
	type: PaymentType;
	variant?: 'page' | 'iframe';
}

const Iframe: React.FC<Props> = ({
	type,
	frameInfo,
	onClick,
	isPreOpened = false,
	children,
	headerTitle,
	gatewayTitle,
	onPaymentFrameLoaded,
	isLoading,
	variant,
	onStartRedirecting,
	openFrameInNewTab
}) => {
	const { Card: css } = useTheme('PaymentForm');
	const { t } = useTranslation('PaymentForm');

	const {
		global: {
			companyInfo: { iataCode }
		}
	} = useConfig();

	const frameRef = React.useRef<HTMLIFrameElement>();
	// Ipad fix
	const { toggle: changeFrameState, isOpen: frameChanged } = useToggleable(false);
	frameRef?.current?.setAttribute('allowpaymentrequest', '');

	useEffect(() => {
		let interval: NodeJS.Timeout | undefined = undefined;
		if (frameRef.current) {
			interval = setInterval(() => {
				try {
					if (frameRef?.current?.contentWindow) {
						if (frameRef.current.contentWindow.location.hash) {
							clearInterval(interval);
							window.location = frameRef.current.contentWindow.location;
						}
					}
				} catch {}
			}, 100);
		}

		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [frameRef, isLoading, frameInfo.frameUrl, frameChanged]);

	const iframeSandbox = useMemo(() => {
		if (iataCode === 'DV') {
			return null;
		}

		return 'allow-downloads allow-same-origin allow-popups allow-top-navigation allow-forms allow-scripts allow-payment-request';
	}, [iataCode]);

	const handleOnClick = useCallback(() => {
		if (variant === 'page' && isCorrectFrameUrl(frameInfo)) {
			onStartRedirecting?.();
			window.location.href = frameInfo.frameUrl;
		} else {
			onClick({
				type,
				title: isPreOpened ? t('Payment') : headerTitle ?? t('Pay by credit or debit card'),
				content: isLoading ? (
					<SimpleLoader />
				) : isCorrectFrameUrl(frameInfo) ? (
					<iframe
						data-test-id="payment-iframe"
						onLoad={() => {
							onPaymentFrameLoaded?.();
							changeFrameState();
						}}
						ref={frameRef}
						src={frameInfo.frameUrl}
						sandbox={iframeSandbox}
						className={css.iframe}
						height={frameInfo.height}
					/>
				) : null
			});
		}
	}, [onClick, frameInfo, variant, iframeSandbox, isPreOpened, headerTitle]);

	useEffect(() => {
		if (openFrameInNewTab && isCorrectFrameUrl(frameInfo)) {
			window.location.href = frameInfo.frameUrl;
		}
	}, [openFrameInNewTab, frameInfo]);

	useEffect(() => {
		if (isPreOpened && frameInfo?.frameUrl) {
			handleOnClick();
		}
	}, [isPreOpened, frameInfo?.frameUrl]);

	const { Gateway: GatewayOverride = Gateway } = useRenderers();

	return (
		<GatewayOverride
			onClick={handleOnClick}
			header={children}
			type={gatewayTitle ?? t('Credit or debit card')}
			paymentType={type}
		/>
	);
};

export default Iframe;
